import { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { useSearchParams, useBlocker } from 'react-router-dom';
import {
  InformationCircleIcon,
  ArrowRightStartOnRectangleIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import { useProblemSession } from '../hooks/useProblemSession';
import { useNavigationGuard } from '../hooks/useNavigationGuard';
import { useLegalBids } from '../hooks/useLegalBids';
import { useAddToReview } from '../hooks/useAddToReview';
import { useOrientationScrollReset } from '../hooks/useOrientationScrollReset';
import { useSessionGuard } from '../contexts/SessionGuardContext';
import { useAuth } from '../hooks/useAuth';
import { usePageTitle } from '../hooks/usePageTitle';

import { registerNavigationGuardClear, unregisterNavigationGuardClear } from '../api/client';
import DifficultySelector from '../components/problems/DifficultySelector';
import { DifficultyRangeControl } from '../components/problems/DifficultyRangeControl';
import {
  DIFFICULTY_RANGE_INLINE,
  DIFFICULTY_RANGE_COMPACT,
} from '../components/problems/difficultyRangeGates';
import type { AnswerResult, DifficultyCategory, SessionOrigin } from '../types/problems';
import { SESSION_ORIGIN_CONFIG, DIFFICULTY_CATEGORIES } from '../types/problems';
// DEFERRED: Timer functionality pending client clarification - UI hidden, problems display unlimited time
// import { Timer } from '../components/problems/Timer';
import { ExpertFeedbackSection } from '../components/problems/ResultFeedback';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
// Feature 023 (T024, FR-029–032) — the shared table-practice frame and its panels. `/problems`
// hosts the SAME components module practice does, so a layout rule is fixed once for both.
import { TableFrame } from '../components/table-session/TableFrame';
import { SessionViewport } from '../components/table-session/SessionViewport';
import { HandPanel } from '../components/table-session/HandPanel';
import { AuctionPanel } from '../components/table-session/AuctionPanel';
import { CallPad } from '../components/table-session/CallPad';
import { PreviousButton } from '../components/table-session/PreviousButton';
import { ExplainPanel, hasExpertComment, explainVariantFor } from '../components/table-session/ExplainPanel';
import { ResultFeedback } from '../components/table-session/ResultFeedback';
import { StatusLine } from '../components/table-session/StatusLine';
import { MastheadInfoRow } from '../components/table-session/MastheadInfoRow';
import { SessionDetailsPopup } from '../components/table-session/SessionDetailsPopup';
import { DetailsPopup } from '../components/table-session/DetailsPopup';
import {
  MASTHEAD_ICON_BUTTON,
  MASTHEAD_EXIT_TAB,
  MASTHEAD_FULL_INLINE,
  MASTHEAD_FULL_BLOCK,
  MASTHEAD_PHONE_BLOCK,
  MASTHEAD_PHONE_FLEX,
} from '../components/table-session/mastheadChrome';
import { BidText } from '../components/common/BidToken';
import { formatBidToken } from '../utils/formatBidToken';
import {
  resolveThirdLevelSubMode,
  thirdLevelSubModePlain,
  ThirdLevelSubModeLabel,
} from '../components/problems/ThirdLevelSubModeLabel';
import { ThirdLevelEditorToolbar } from '../components/problems/ThirdLevelEditorToolbar';
import { ResumePromptModal } from '../components/problems/ResumePromptModal';
import { AssignDifficultyControls } from '../components/overcalling-assign-difficulty/AssignDifficultyControls';
import { isEditorRole } from '../types/auth';

// DEFERRED: Set to true to re-enable timer functionality when client requirements are clarified
const TIMER_ENABLED = false;

/**
 * Overcalling names itself in two parts — the family it belongs to, then the side of the auction
 * it drills. Desktop joins them into one line; the phone banner sets the family above the name in
 * the muted-over-bold form module practice uses for its course trail, so a learner reads the same
 * two things in the same order on either surface.
 */
const OVERCALLING_TRAIL = 'Competitive Bidding';
const OVERCALLING_NAME = 'The overcalling side';

/**
 * The frame's h1 per mode. Partial on purpose: `SessionOrigin` still carries origins this page
 * never reaches (`dashboard`, the deprecated `component-selector`, and the `practice-*` values
 * left over from the Precision surface T043 retired), and naming them here would imply this page
 * serves them. Anything unlisted falls back to the generic title.
 *
 * A level-practice session prefixes its chosen level ("Beginner · Bidding Practice") — see
 * `modeTitle` below. The other modes carry no level, so they read as they always have.
 */
const MODE_TITLES: Partial<Record<SessionOrigin, string>> = {
  'difficulty-selector': 'Bidding Practice',
  'important-sequences': 'Bidding Practice',
  'review-problems': 'Review Problems',
  overcalling: `${OVERCALLING_TRAIL} · ${OVERCALLING_NAME}`,
  '3rd-level-bid': 'Decision to make a 3rd level bid',
};

export function ProblemsPage() {
  const [searchParams] = useSearchParams();
  usePageTitle(
    searchParams.get('mode') === '3rd-level-bid'
      ? 'Decision to make a 3rd level bid — Hawkes Bridge'
      : 'Problems — Hawkes Bridge',
  );
  const {
    currentProblem,
    currentIndex,
    totalProblems,
    netScore, // T026: Net score for display
    streak, // Feature 023: the current run, shown beside the score
    isLoading,
    error, // For displaying error messages (e.g., no problems available)
    lastResult,
    showingResult,
    isSessionComplete,
    startSession,
    submitAnswer,
    skipProblem,
    nextProblem,
    navigateToSummary,
    // T045-T046: Mid-session range adjustment state
    originalCategory,
    currentRange,
    // T047: Mid-session range adjustment action
    updateDifficultyRange,
    // T051: No problems in range flag
    noProblemsInRange,
    // T064: User's last submitted bid for "You bid:" display
    lastUserBid,
    // v1.4: Session origin for mode-specific behavior
    sessionOrigin,
    // v1.4: Remove from review action
    removeFromReviewAndNext,
    // Feature 026: continue-or-restart prompt
    resumePrompt,
    continueFromSavedPosition,
    restartFromBeginning,
    // v1.5: Reset session for navigation away confirmation
    resetSession,
    // Feature 013 editor mode (Session 2026-06-15, FR-027–FR-031)
    isThirdLevelEditor,
    editorCategory,
    setEditorCategory,
    refreshThirdLevelEditor,
    assignThirdLevelDifficulty,
    goToPreviousProblem,
  } = useProblemSession();

  // T090: Get mode from URL query parameter
  const urlMode = searchParams.get('mode');
  const effectiveMode: SessionOrigin = useMemo(() => {
    if (urlMode === 'important-sequences') return 'important-sequences';
    if (urlMode === 'review') return 'review-problems';
    if (urlMode === '3rd-level-bid') return '3rd-level-bid';
    return sessionOrigin ?? 'difficulty-selector';
  }, [urlMode, sessionOrigin]);

  // Get configuration for the current mode
  const modeConfig = SESSION_ORIGIN_CONFIG[effectiveMode];

  // Feature 013 editor mode (Session 2026-06-15, FR-027): the inline authoring controls render
  // only for Admin/Editor on the "Decision to make a 3rd level bid" page. Public learners and all
  // other modes never see them. Editor mode is exempt from the accessibility/phone-layout scope.
  const isEditorMode = effectiveMode === '3rd-level-bid' && isThirdLevelEditor;

  // Feature 025's "Ask Jeff" button applied to the Overcalling page's tournament HANDS only. Since
  // feature 033 that page serves teaching problems alone (the hands moved to the Overcalls
  // inventory's module-practice rows, which carry their own Ask Jeff button), so no item here
  // qualifies and the footer action is gone. The 3rd-level page has its own comment workflow.

  // Feature 023 (T024): the hand's identifier leads the status line for Admin/Editor only — a
  // learner has no use for a database id, but an editor tracing a reported hand needs it first.
  // Same rule and same gate as module practice, which shows it through the same component.
  const { user, logout } = useAuth();
  const canSeeHandId = user?.role === 'Admin' || isEditorRole(user?.role);

  // Feature 013 (Session 2026-06-15): an already-answered hand in editor mode is shown read-only
  // in the SAME result format every practice page uses after submitting (the shared
  // ExpertFeedbackSection), NOT a bespoke panel. Synthesize the result from the persisted
  // answer + Jeff's actual call + the server-computed verdict. Only when not already showing a
  // freshly-submitted result.
  const answeredReview = isEditorMode && !showingResult && !!currentProblem?.isAnswered;
  const reviewResult: AnswerResult | null =
    answeredReview && currentProblem
      ? {
          isCorrect: !!currentProblem.editorAnswerCorrect,
          correctBid: currentProblem.jeffBid ?? '',
          expertName: 'Jeff',
          // FR-034 (Session 2026-07-01): show Jeff's comment in the editor read-only review of an
          // answered hand, just as a fresh submit does. The server sends jeffComment only for
          // answered hands that carry one; empty string keeps the "Jeff says:" section hidden.
          expertComment: currentProblem.jeffComment ?? '',
        }
      : null;

  // The verdict currently on screen, whichever branch produced it.
  const shownResult = answeredReview ? reviewResult : lastResult;

  // Feature 027 — does the AI commentary panel apply to what was just answered?
  //
  // Only downloaded tournament hands qualify: local problems carry curated commentary of their
  // own, which is the void this feature exists to fill (FR-007). Since feature 033 one mode
  // serves such hands here — every 3rd-level hand IS one, so the mode alone settles it. (The
  // Overcalling page used to mix hands in and flagged them on the answer response; it is
  // problems-only now, and the response's `isTournamentHand` is always false there.)
  //
  // Restricted to a freshly-submitted result: the editor's read-only review of an
  // already-answered hand synthesizes its result from stored fields with no AI lookup behind it,
  // and "Information not available" would there mean "we never asked" rather than "the engine
  // did not predict this call".
  const explainVariant = explainVariantFor({
    // Restricted to a freshly-submitted result: the editor's read-only review of an
    // already-answered hand synthesizes its result from stored fields with no AI lookup behind
    // it, and "Information not available" would there mean "we never asked".
    answered: !answeredReview && !!lastResult,
    expertComment: shownResult?.expertComment,
    isTournamentHand: effectiveMode === '3rd-level-bid',
  });

  // Feature 013 (Session 2026-06-10): the sub-mode the user navigated from, shown under the
  // header. Only the "Decision to make a 3rd level bid" page carries a sub-mode; other modes (and
  // the dev by-id locator, which has no subMode) leave this null so their headers are untouched.
  const thirdLevelSubMode = useMemo(
    () =>
      effectiveMode === '3rd-level-bid'
        ? resolveThirdLevelSubMode(searchParams.get('subMode'), searchParams.get('openingBid'))
        : null,
    [effectiveMode, searchParams],
  );

  // Plain-text version, appended to the frame's accessible title so screen readers announce the
  // sub-mode alongside the page title.
  const subModeAriaSuffix = thirdLevelSubModePlain(thirdLevelSubMode);

  // What this session calls itself, in the frame's masthead and — via the session guard — in the
  // site header's phone banner. A level-practice session names the level it was started at (client
  // request): "Beginner · Bidding Practice". It reads from `originalCategory`, NOT the live slider
  // range, so widening the range mid-session does not rewrite the title of the session the learner
  // chose; the categories overlap anyway (Beginner 1-2 sits inside Intermediate 1-4), so a range
  // does not identify a level. Every other mode reads as it always has.
  const modeTitle = useMemo(
    () =>
      effectiveMode === 'difficulty-selector' && originalCategory
        ? `${DIFFICULTY_CATEGORIES[originalCategory].name} · Bidding Practice`
        : (MODE_TITLES[effectiveMode] ?? 'Bidding Practice'),
    [effectiveMode, originalCategory],
  );

  // T135-T139: Navigation away confirmation for active sessions (v1.5)
  // State to track if we're intentionally navigating to summary (to skip confirmation)
  const [isNavigatingToSummary, setIsNavigatingToSummary] = useState(false);

  // FR-109 (v1.9): Logout confirmation during active session
  const {
    setHasActiveSession,
    setLogoutInterceptor,
    setScoringType,
    setSessionTitle,
    setSessionTitleTrail,
  } = useSessionGuard();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Ref for the main problems scroll container (used for orientation change reset)
  const problemsScrollRef = useRef<HTMLDivElement>(null);

  // Feature 023: the phone ⓘ popup carrying the facts the desktop status line shows. Phones hide
  // the status line, so this is the only place those words exist at that width.
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);

  // The difficulty range's other home (client contract, 2026-07-30): on the bands whose height the
  // frame's controls row would eat, that row is not drawn at all and the range is reached from the
  // masthead instead. The two popups are mutually exclusive — they open over the same corner of a
  // ~360px screen, and the masthead's whole point is that the strip stays one row.
  const [showDifficultyPopup, setShowDifficultyPopup] = useState(false);
  const openDetailsPopup = useCallback(() => {
    setShowDifficultyPopup(false);
    setShowDetailsPopup(true);
  }, []);
  const openDifficultyPopup = useCallback(() => {
    setShowDetailsPopup(false);
    setShowDifficultyPopup(true);
  }, []);

  // Feature 023: pinning the current problem to the review list. Held here, not in the buttons,
  // because the desktop status-line pin and the phone panel pin are the same action. `enabled`
  // is the mode gate: only the modes that draw a pin read the review list to seed it.
  const {
    isPinned: addedToReview,
    isPinning: isAddingToReview,
    addToReview: handleAddToReview,
  } = useAddToReview(currentProblem?.id, { enabled: modeConfig.allowAddToReview });

  // T032-T034: Track selected bid level for dynamic suit filtering
  const [selectedBidLevel, setSelectedBidLevel] = useState<number | null>(null);

  // T014-T015: Compute legal bids based on auction state
  const legalBids = useLegalBids({
    auction: currentProblem?.auction ?? [],
    userPosition: currentProblem?.decisionSeat ?? 'S',
    selectedLevel: selectedBidLevel,
  });

  // T034: Reset selected level when moving to next problem
  useEffect(() => {
    setSelectedBidLevel(null);
  }, [currentProblem?.id]);

  // T034: Callback for level selection change
  const handleBidLevelChange = useCallback((level: number | null) => {
    setSelectedBidLevel(level);
  }, []);

  // Determine if session is active (has started and not complete)
  // FR-072: Only block when there's an active session with remaining work
  // FIX: Check for problems loaded (works for all modes: difficulty-selector, important-sequences)
  // v1.8 (FR-106): Review mode now has navigation guard like other modes
  const hasActiveSession = !!(
    totalProblems > 0 &&
    !isSessionComplete &&
    !isNavigatingToSummary &&
    modeConfig.hasNavGuard
  );

  // T135: Use navigation guard hook for browser beforeunload events (FR-067, FR-068)
  useNavigationGuard(hasActiveSession);

  // FR-109 (v1.9): Set session guard for logout confirmation
  // FR-114 (v3.0): Register navigation guard clear callback for error redirects
  useEffect(() => {
    setHasActiveSession(hasActiveSession);
    setScoringType(hasActiveSession ? (currentProblem?.scoring ?? null) : null);
    // Feature 023: the header's phone banner reads this rather than keeping its own copy of the
    // mode→label mapping, so it can never disagree with the frame's masthead. Overcalling hands
    // over its two parts separately so the banner can stack them; every other mode sends the whole
    // title as the one bold line it has always been.
    const isOvercalling = effectiveMode === 'overcalling';
    setSessionTitle(hasActiveSession ? (isOvercalling ? OVERCALLING_NAME : modeTitle) : null);
    setSessionTitleTrail(hasActiveSession && isOvercalling ? OVERCALLING_TRAIL : null);
    if (hasActiveSession) {
      setLogoutInterceptor(() => setShowLogoutConfirm(true));

      // FR-114: Register callback to clear navigation guard before error redirects
      // This prevents the "Leave site?" popup during session expiration or server errors
      registerNavigationGuardClear(() => {
        // Clear the blocker by proceeding, and reset session state
        resetSession();
        // Force the blocker to not block by setting flag
        setIsNavigatingToSummary(true);
      });
    } else {
      setLogoutInterceptor(null);
      unregisterNavigationGuardClear();
    }
    // Cleanup on unmount
    return () => {
      setHasActiveSession(false);
      setScoringType(null);
      setSessionTitle(null);
      setSessionTitleTrail(null);
      setLogoutInterceptor(null);
      unregisterNavigationGuardClear();
    };
  }, [hasActiveSession, setHasActiveSession, setLogoutInterceptor, setScoringType, setSessionTitle, setSessionTitleTrail, effectiveMode, modeTitle, currentProblem?.scoring, resetSession]);

  // FR-109 (v1.9): Handle confirming logout during active session
  const handleConfirmLogout = useCallback(() => {
    setShowLogoutConfirm(false);
    resetSession(); // Clear localStorage and session state
    logout();
  }, [resetSession, logout]);

  // FR-109 (v1.9): Handle canceling logout
  const handleCancelLogout = useCallback(() => {
    setShowLogoutConfirm(false);
  }, []);

  // T136: Use React Router's useBlocker for internal navigation (FR-069)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => {
      // T139: Skip confirmation when navigating to summary page (session ending naturally)
      if (nextLocation.pathname === '/session-summary') {
        return false;
      }
      // Block if pathname changes
      if (hasActiveSession && currentLocation.pathname !== nextLocation.pathname) {
        return true;
      }
      // FIX: Also block if we're on /problems and query params change (switching Problem modes)
      if (hasActiveSession &&
          currentLocation.pathname === '/problems' &&
          nextLocation.pathname === '/problems' &&
          currentLocation.search !== nextLocation.search) {
        return true;
      }
      return false;
    }
  );

  // T138: Handle confirming navigation away - clear localStorage and proceed
  const handleConfirmLeave = useCallback(() => {
    // FR-070: Reset session state (including localStorage) before navigation proceeds
    // FIX: Use resetSession() to clear both hook state and localStorage,
    // allowing new mode to start fresh when navigating between Problems options
    resetSession();
    blocker.proceed?.();
  }, [blocker, resetSession]);

  // T137: Handle canceling navigation away - stay on page
  const handleCancelLeave = useCallback(() => {
    // FR-071: Keep user on problem-display page with session intact
    blocker.reset?.();
  }, [blocker]);

  // T139: Wrapper for navigateToSummary that sets flag to skip confirmation.
  // `modeTitle` travels with it so the scorecard's trail reads exactly as the masthead above
  // the table did — including the level a difficulty session named itself with.
  const handleNavigateToSummary = useCallback(() => {
    setIsNavigatingToSummary(true);
    navigateToSummary(modeTitle);
  }, [navigateToSummary, modeTitle]);

  // Feature 023: moving off the hand dismisses the phone details popup — its facts (position,
  // difficulty, the run) belong to the hand that was on screen when it was opened.
  const handleNext = useCallback(() => {
    setShowDetailsPopup(false);
    nextProblem();
  }, [nextProblem]);

  const handleSkip = useCallback(() => {
    setShowDetailsPopup(false);
    void skipProblem();
  }, [skipProblem]);

  // Back to the problem already dealt with, which comes back read-only with the verdict it
  // earned (client request, 2026-08-03). Same popup dismissal as moving forward — the ⓘ facts
  // belong to the hand that was on screen when it was opened, whichever way the learner leaves it.
  const handlePrevious = useCallback(() => {
    setShowDetailsPopup(false);
    goToPreviousProblem();
  }, [goToPreviousProblem]);

  // Navigate to summary when session is complete (for modes that navigate to Summary)
  // v1.8 (FR-107): Review mode now also navigates to Summary page
  // Also check totalProblems > 0 to avoid navigating during mode switch when state is resetting
  useEffect(() => {
    if (isSessionComplete && modeConfig.navigatesToSummary && totalProblems > 0) {
      setIsNavigatingToSummary(true);
      navigateToSummary(modeTitle);
    }
  }, [isSessionComplete, navigateToSummary, modeConfig.navigatesToSummary, totalProblems, modeTitle]);

  // T018: Handle difficulty selection and start session
  // T045: Pass category for mid-session slider bounds tracking
  const handleStartSession = useCallback((minDifficulty: number, maxDifficulty: number, category?: DifficultyCategory) => {
    startSession({ minDifficulty, maxDifficulty }, category);
  }, [startSession]);

  // T048-T049: Handle mid-session range adjustment (constrained to category bounds)
  const handleRangeChange = useCallback((minValue: number, maxValue: number) => {
    updateDifficultyRange(minValue, maxValue);
  }, [updateDifficultyRange]);

  // T048 UPDATED 2026-01-12: Mid-session slider shown for ALL categories (FR-027)
  // Slider always shows 1-10 range regardless of category (FR-028)
  // FIX 2026-01-12: Show slider when in active session even if currentProblem is null (noProblemsInRange)
  // T096: Hide slider when session origin doesn't allow it (important-sequences, review-problems)
  const isInActiveSession = originalCategory && currentRange;
  const showMidSessionSlider = isInActiveSession && modeConfig.showSlider;
  // Disable slider in serial search mode (read-only, for QA layout testing)
  const isSerialSearch = !!searchParams.get('serial');

  // Scroll to top when transitioning from DifficultySelector to problem display
  useEffect(() => {
    if (currentProblem || isInActiveSession) {
      window.scrollTo(0, 0);
    }
  }, [!!currentProblem, !!isInActiveSession]);

  // Reset scroll position on orientation change (iOS Safari: controls hide behind sticky header).
  // Shared with the other table sessions — the behaviour travels with the frame.
  useOrientationScrollReset(problemsScrollRef);

  // v1.8 (FR-100, FR-101): Important Sequences no longer shows completion view.
  // Backend now returns ALL important sequence problems (no history exclusion),
  // so the session restarts from the beginning when accessed again.
  // Only show error message if no important sequence problems exist in the database at all.
  if (effectiveMode === 'important-sequences' && !isLoading && error && totalProblems === 0) {
    return (
      <SessionViewport>
        <div className="flex-1 bg-felt-bg px-4 py-12 text-center">
          <h2 className="mb-4 text-2xl font-bold text-cream">
            No Important Sequences Available
          </h2>
          <p className="mx-auto mb-6 max-w-xl text-sand">
            {error}
          </p>
          <a
            href="/dashboard"
            className="inline-block rounded-md bg-brass px-6 py-3 font-medium text-on-brass hover:bg-brass-hover"
          >
            Back to Dashboard
          </a>
        </div>
      </SessionViewport>
    );
  }

  // v1.8 (FR-107): Review mode now navigates to Summary page when session is complete.
  // This view only shows when review list is empty (no problems to review).
  // isSessionComplete is handled by the useEffect that navigates to Summary page.
  if (effectiveMode === 'review-problems' && !isLoading && totalProblems === 0 && !isSessionComplete) {
    return (
      <SessionViewport>
        <div className="flex-1 bg-felt-bg px-4 py-12 text-center">
          <h2 className="mb-4 text-2xl font-bold text-cream">
            No Problems to Review
          </h2>
          <p className="mx-auto mb-6 max-w-xl text-sand">
            Your review list is empty. Add problems to review while practicing.
          </p>
          <a
            href="/dashboard"
            className="inline-block rounded-md bg-brass px-6 py-3 font-medium text-on-brass hover:bg-brass-hover"
          >
            Back to Dashboard
          </a>
        </div>
      </SessionViewport>
    );
  }

  // Feature 013: empty-state for the PUBLIC "Decision to make a 3rd level bid" page when no
  // eligible items are available (or the pool failed to load). Must precede the difficulty-selector
  // view below, or a hands-only mode with no items would fall through to the difficulty picker.
  // Editor mode is handled separately just below (it keeps the category toolbar so the editor can
  // switch to a non-empty category in place — see the next branch).
  if (effectiveMode === '3rd-level-bid' && !isEditorMode && !isLoading && totalProblems === 0) {
    return (
      <SessionViewport>
        <div className="flex-1 bg-felt-bg px-4 py-12 text-center">
          <h2 className="mb-4 text-2xl font-bold text-cream">
            No 3rd Level Bid Decisions Available
          </h2>
          <p className="mx-auto mb-6 max-w-xl text-sand">
            {error ?? 'There are no qualifying hands to practise right now.'}
          </p>
          <a
            href="/dashboard"
            className="inline-block rounded-md bg-brass px-6 py-3 font-medium text-on-brass hover:bg-brass-hover"
          >
            Back to Dashboard
          </a>
        </div>
      </SessionViewport>
    );
  }

  // Feature 013 editor mode: when the SELECTED data category has no hands, stay on the page and
  // keep the category toolbar visible (FR-028) so the editor can pick another category in place
  // — selecting one re-fetches and renders its hands. No "Back to Dashboard" bounce; the message
  // is shown inline. Editor mode is desktop/tablet only, so the desktop header + toolbar suffice.
  if (isEditorMode && !isLoading && totalProblems === 0) {
    return (
      <div className="mx-auto px-2 md:px-4 py-8" style={{ maxWidth: 'min(100vw - 2rem, 75rem)' }}>
        <div className="flex items-center gap-4 mb-5">
          <div className="flex flex-col min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-ink leading-tight">
              Decision to make a 3rd level bid
            </h1>
            {thirdLevelSubMode && (
              <span className="text-sm font-medium text-ink-soft mt-0.5 truncate" data-testid="sub-mode-label">
                <ThirdLevelSubModeLabel descriptor={thirdLevelSubMode} />
              </span>
            )}
          </div>
        </div>

        <div data-testid="third-level-editor-controls">
          <ThirdLevelEditorToolbar
            category={editorCategory}
            onCategoryChange={setEditorCategory}
            onRefresh={refreshThirdLevelEditor}
            isAnswered={false}
            isAssigned={false}
            disabled={isLoading}
          />
        </div>

        <div
          className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center"
          data-testid="third-level-editor-empty"
        >
          <p className="text-yellow-800 font-medium">
            No hands available in the “{editorCategory}” category.
          </p>
          <p className="text-ink-soft text-sm mt-1">
            Select another category above to see its hands.
          </p>
        </div>
      </div>
    );
  }

  // Difficulty Selection View (T018: replaced ComponentSelector with DifficultySelector)
  // FIX 2026-01-12: Only show DifficultySelector if NOT in an active session
  // FIX 2026-01-13: Removed error display from this view - errors from other modes (e.g., review)
  // should not appear on the difficulty selector. Errors specific to difficulty selection
  // are handled by the DifficultySelector component or shown after session starts.
  if (!currentProblem && !isLoading && !isInActiveSession) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-ink mb-8 text-center">Bidding Problems Practice</h1>

        <DifficultySelector
          onStartSession={handleStartSession}
          isLoading={isLoading}
        />
      </div>
    );
  }

  // Loading View. Inside the viewport lock on the felt, like the other table sessions: the page
  // must not change height between dealing and the table being ready.
  if (isLoading && !currentProblem) {
    return (
      <SessionViewport>
        <div className="flex-1 bg-felt-bg px-4 py-16 text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-brass"></div>
          <p className="mt-4 text-cream">Loading problems...</p>
        </div>
      </SessionViewport>
    );
  }

  // ---------------------------------------------------------------------------------------
  // Problem Display View — "The Table" (feature 023, T024 / FR-029–032).
  //
  // Every mode (level practice, important sequences, review, overcalling, 3rd-level bid) is
  // drawn by the SAME TableFrame module practice uses, and since the difficulty control moved
  // inside the frame this page takes the same phone treatment too (see TABLE_SESSION_ROUTES in
  // Layout.tsx): a compact masthead info row and its ⓘ popup below md, the full status line from
  // md up, and the three-panel row on a landscape phone.
  //
  // FIX 2026-01-12: show the session view whenever a session is active, even if currentProblem
  // is null because nothing matched the range (noProblemsInRange).
  // ---------------------------------------------------------------------------------------
  if (currentProblem || isInActiveSession) {
    // The way out of the session. Phones collapse it to the shared brass exit tab — the same
    // control module practice uses for "Leave the Table", at the same height as the ⓘ beside it.
    const mastheadLinks = modeConfig.showsEndSession ? (
      <button
        type="button"
        onClick={handleNavigateToSummary}
        aria-label="End Session"
        title="End Session"
        className={MASTHEAD_EXIT_TAB + ' font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-cream'}
      >
        <span className={MASTHEAD_FULL_INLINE}>End Session</span>
        <ArrowRightStartOnRectangleIcon className={`h-5 w-5 ${MASTHEAD_PHONE_BLOCK}`} aria-hidden="true" />
      </button>
    ) : undefined;

    // The ⓘ trigger trails the masthead's compact info row, expanding it into the words the status
    // line shows on desktop. Phone-only: from md up the status line itself is on screen.
    const mastheadDetailsButton = (
      <button
        type="button"
        onClick={openDetailsPopup}
        aria-label="Problem details"
        title="Problem details"
        className={`${MASTHEAD_ICON_BUTTON} text-cream ${MASTHEAD_PHONE_BLOCK}`}
        data-testid="masthead-details-button"
      >
        <InformationCircleIcon className="h-5 w-5" aria-hidden="true" />
      </button>
    );

    // The difficulty range, at the viewports where the frame has no height to draw it inline:
    // medium-and-shorter phones in both orientations, plus the short-but-wide ones a big screen
    // reaches at 200% zoom. DIFFICULTY_RANGE_COMPACT is the exact complement of the gate on the
    // controls row below — see difficultyRangeGates.ts for the band-by-band table it came from.
    //
    // It states its own value (the "1-4" beside the glyph) rather than being a bare icon: the range
    // in force is a fact the learner needs while answering, and this is the only place it is
    // written at these widths. The numbers are brass-on-felt (4.9:1); the glyph is cream.
    const mastheadDifficultyButton =
      showMidSessionSlider && currentRange ? (
        <button
          type="button"
          onClick={openDifficultyPopup}
          aria-label={`Difficulty range ${currentRange.minLevel} to ${currentRange.maxLevel} — adjust`}
          title="Difficulty range"
          aria-haspopup="dialog"
          aria-expanded={showDifficultyPopup}
          // The xnarrow min-size holds the 24 × 24 hard floor (FR-021a / WCAG 2.5.8): below 360px
          // the glyph shrinks to 16px and the range label is hidden, which left the button 20 × 20
          // — the one masthead control under the floor. Stating the minimum directly (rather than
          // re-padding) leaves the icon-button padding scale alone and lands the button on the same
          // 24px the ⓘ beside it already measures.
          className={`${MASTHEAD_ICON_BUTTON} flex items-center justify-center gap-1 text-cream xnarrow:min-h-[24px] xnarrow:min-w-[24px] ${DIFFICULTY_RANGE_COMPACT}`}
          data-testid="masthead-difficulty-button"
        >
          <AdjustmentsHorizontalIcon className="h-5 w-5 xnarrow:h-4 xnarrow:w-4" aria-hidden="true" />
          {/* Below 360px the strip is already carrying the info row, the ⓘ and the exit tab, and a
              fourth item wraps the masthead onto a second line — which costs more height than the
              control it is standing in for. The glyph alone survives, and the range stays in the
              button's accessible name and in the popup it opens. Same trade the gauge's "N/10"
              label makes in the compact row. */}
          <span className="text-xs font-bold text-brass-on-felt xnarrow:hidden">
            {currentRange.minLevel}-{currentRange.maxLevel}
          </span>
        </button>
      ) : undefined;

    // The difficulty is a post-answer reveal, except in editor mode where the assigned level is
    // always shown so editors can re-assign without answering first (FR-031).
    const revealDifficulty = showingResult || answeredReview || isEditorMode;

    // Whether this problem can be pinned to the review list at all: the mode must allow it, and
    // there must be a verdict on screen (pinning is how a learner keeps a problem they just got
    // wrong). Both controls — the status-line pin and the phone one — share this gate.
    const canPinToReview = !!(modeConfig.allowAddToReview && currentProblem && (showingResult || answeredReview));

    // The review pin, for the widths where the status line that normally carries it is
    // display:none. Same action, same state, same words as the pin under the frame — handed to
    // ExpertFeedbackSection's action row so it sits BESIDE "Next Problem" rather than on a line
    // of its own beneath it (client request, 2026-07-30: one button row, not two).
    const phoneReviewPin = canPinToReview ? (
      <button
        type="button"
        onClick={handleAddToReview}
        disabled={isAddingToReview || addedToReview}
        aria-pressed={addedToReview}
        data-testid="phone-review-pin"
        className={
          'rounded-lg border border-brass bg-cream px-4 py-2.5 text-[15px] font-bold text-brass-text ' +
          'short:px-3 short:py-1 short:text-sm ' +
          'hover:bg-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-brass ' +
          'disabled:cursor-not-allowed disabled:opacity-60 ' + MASTHEAD_PHONE_BLOCK
        }
      >
        <span aria-hidden="true">📌</span> {addedToReview ? 'In Review' : 'Add to Review'}
      </button>
    ) : undefined;

    // "Previous" (client request, 2026-08-03) — on EVERY mode this page serves, since every one
    // of them is a door onto the same table: level practice, Important Sequences, Review,
    // Overcalling and the 3rd-level bid. Inert at the first problem, so it appears only once
    // there is something behind to go back to.
    //
    // Editor mode is the one exception, and only because it already has this control: FR-030's
    // right-aligned Previous sits below the frame with the assign row it belongs to. Drawing a
    // second one inside the panel would put two Previouses on one screen.
    const showPrevious = !isEditorMode && currentIndex > 0;

    // Beside "Next Problem" in the verdict row: natural width, and it already matches that
    // button's height at every band (both carry the same `short:` step).
    const previousButton = showPrevious ? (
      <PreviousButton onClick={handlePrevious} disabled={isLoading} />
    ) : undefined;

    // Beside "Skip Problem" on the pad: an equal share of the row, with the height coming from the
    // row's `items-stretch` — Skip carries no `short:` step and is the taller of the two on a
    // phone. Where the label is hidden there is nothing to give width to, so it falls back to its
    // own size and leaves the room to Skip.
    const previousButtonWide = showPrevious ? (
      <PreviousButton
        onClick={handlePrevious}
        disabled={isLoading}
        className="flex-1 flat-ls:flex-none xnarrow:flex-none"
      />
    ) : undefined;

    // The verdict, then the buttons. ExpertFeedbackSection is reused in buttonsOnly mode rather
    // than reimplemented: Next Problem plus the Remove-from-Review flow (with its confirmation
    // dialog) is real behaviour that belongs in one place. Add-to-Review is NOT there — it is the
    // status-line pin from md up, and the phone button above, which are the same action and share
    // one piece of state (useAddToReview).
    const callPanel =
      showingResult || answeredReview ? (
        <div className="call-stack space-y-2" data-testid="problem-verdict">
          {shownResult && (
            <ResultFeedback
              isCorrect={shownResult.isCorrect}
              answer={<BidText text={formatBidToken(shownResult.correctBid ?? '')} />}
              userCall={
                (answeredReview ? currentProblem?.editorAnswer : lastUserBid)
                  ? <BidText text={formatBidToken((answeredReview ? currentProblem?.editorAnswer : lastUserBid) as string)} />
                  : undefined
              }
            />
          )}
          {!shownResult && (
            <p className="text-[15px] font-bold text-ink-soft" data-testid="problem-skipped">
              Skipped
            </p>
          )}
          <ExpertFeedbackSection
            result={shownResult}
            wasSkipped={!answeredReview && lastResult === null}
            onNext={handleNext}
            problemId={currentProblem?.id}
            sessionOrigin={effectiveMode}
            onRemoveFromReview={removeFromReviewAndNext}
            userBid={answeredReview ? currentProblem?.editorAnswer : lastUserBid}
            buttonsOnly={true}
            leadingActions={previousButton}
            extraActions={phoneReviewPin}
          />
        </div>
      ) : (
        // `call-stack` is the CSS hook the height-starved bands tighten this column by
        // (index.css). A landscape phone's row is ~130px and this stack is a four-row bid pad
        // plus Skip; its own gaps are real height there.
        <div className="call-stack space-y-3">
          <CallPad
            onSubmit={submitAnswer}
            disabled={isLoading}
            legalBids={legalBids}
            onLevelChange={handleBidLevelChange}
          />
          {/* Previous and Skip share one row rather than stacking: they are the same weight of
              action, and a second full-width button is ~48px the landscape band does not have.
              Skip keeps the remaining width so it stays the wide target it has always been.
              `call-actions` is the hook that keeps the height-starved bands reaching Skip: they
              trim it through `.call-stack > button`, and wrapping it in this row put it a level
              out of that selector's reach — worth 10px of overflow at 754x303, which is the whole
              margin the shortest landscape phone has. */}
          <div className="call-actions flex items-stretch gap-2">
            {previousButtonWide}
            <button
              type="button"
              onClick={handleSkip}
              disabled={isLoading}
              // `px-4` matches Previous beside it: `flex-1` bases the split on the CONTENT box, so
              // each item's own horizontal padding rides on top of its half, and padding on one
              // side only made the two halves unequal.
              className="flex-1 rounded-lg border border-brass bg-cream px-4 py-2.5 text-[15px] font-medium text-ink hover:bg-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-brass disabled:cursor-not-allowed disabled:opacity-50"
            >
              Skip Problem
            </button>
          </div>
        </div>
      );

    // Mid-session difficulty range (FR-027/FR-028), INSIDE the frame's controls slot: it is the
    // session's own control, it spans the panels it filters, and keeping it out of the page above
    // the frame is what let this route take the phone treatment at all (Layout.tsx). On the felt it
    // drops the cream card — a pale slab across the top of the table read as a banner pasted over
    // it — and wears the felt's own accent instead.
    //
    // Drawn only where the frame's height can carry it — DIFFICULTY_RANGE_INLINE, whose bands are
    // the pre-023 page's own measurements (difficultyRangeGates.ts). A tall phone and a landscape
    // phone over 400px keep it, compacted by index.css the way the old page's per-band copies were;
    // the medium-and-shorter phones of both orientations do not, because this page owes an app-like
    // no-scroll contract and there the ~78px comes straight off the panel row. Those get the
    // masthead trigger above instead.
    const difficultyControls =
      showMidSessionSlider && currentRange ? (
        <DifficultyRangeControl
          className="difficulty-slider-inline"
          surface="felt"
          minLevel={currentRange.minLevel}
          maxLevel={currentRange.maxLevel}
          disabled={isSerialSearch}
          onChange={handleRangeChange}
        />
      ) : undefined;

    return (
      <SessionViewport ref={problemsScrollRef}>
        {/* Feature 013 editor mode (FR-028): the "Data category" toolbar + status pills, above
            the frame. Renders only for Admin/Editor on the 3rd-level page, which is desktop-only
            and exempt from the phone-layout scope (FR-027) — hence still page chrome. */}
        {isEditorMode && currentProblem && (
          <div className="mx-auto max-w-[1080px] px-4 pt-4" data-testid="third-level-editor-controls">
            <ThirdLevelEditorToolbar
              category={editorCategory}
              onCategoryChange={setEditorCategory}
              onRefresh={refreshThirdLevelEditor}
              isAnswered={!!currentProblem.isAnswered}
              isAssigned={!!currentProblem.isAssigned}
              disabled={isLoading}
            />
          </div>
        )}

        {/* T051: nothing matched the selected range, so there is no frame to put the control in.
            The slider comes along regardless — widening the range is the way out of this state,
            and without it the only move left would be ending the session.
            Ungated, unlike the frame's row, and it is the one place that is right: there
            is no masthead here to reach a popup from, and nothing else on the page to compete for
            the height — a banner and a control cannot overrun a viewport. */}
        {noProblemsInRange && (
          <div className="flex-1 bg-felt-bg px-4 py-8">
            <div className="mx-auto max-w-[1080px] space-y-4">
              {difficultyControls && (
                <div className="rounded-[10px] bg-felt px-4 py-3 shadow-[0_2px_6px_rgba(0,0,0,.25)]">
                  {difficultyControls}
                </div>
              )}
              <div className="rounded-[10px] border-l-[6px] border-brass bg-notice px-4 py-3 text-notice-ink">
                <p className="font-medium">No problems available in the selected range.</p>
                <p className="mt-1 text-sm text-ink-soft">
                  Adjust the difficulty range or end your session.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Timer - DEFERRED: Hidden pending client clarification on requirements */}
        {TIMER_ENABLED && <div className="mb-6" />}

        {currentProblem && (
          <TableFrame
            // `flex-1` stretches the felt to the bottom of the viewport-locked container (see
            // ModulePracticePage for the full rationale): without it the page's paper background
            // shows through as a pale band under the table whenever the content is shorter than
            // the lock. Phones spend the wrapper's breathing room on content instead.
            // `problems-table` is index.css's hook for making the frame fill its column (Firefox).
            className="problems-table flex-1 bg-felt-bg py-1.5 sm:py-6 padswap:py-0.5"
            subtitle={
              <>
                {/* md+ (and not a landscape phone): the mode title, with the 3rd-level opener
                    slice appended where there is one. */}
                <span className={MASTHEAD_FULL_INLINE}>
                  {thirdLevelSubMode ? (
                    <span data-testid="sub-mode-label">
                      {modeTitle} · <ThirdLevelSubModeLabel descriptor={thirdLevelSubMode} />
                    </span>
                  ) : (
                    modeTitle
                  )}
                </span>
                {/* Phone: the site header banner already names the session, so repeating it in the
                    masthead would spend the row twice. The h1 keeps an sr-only name; what SHOWS is
                    the compact info row — position, id, streak/score, difficulty. */}
                <span className={`sr-only ${MASTHEAD_PHONE_BLOCK}`}>{modeTitle}</span>
                <MastheadInfoRow
                  screens={MASTHEAD_PHONE_FLEX}
                  position={currentIndex + 1}
                  poolSize={totalProblems}
                  handId={canSeeHandId ? (currentProblem.serial ?? currentProblem.id) : null}
                  streak={streak}
                  score={netScore}
                  difficulty={currentProblem.difficultyLevel}
                  answered={revealDifficulty}
                />
              </>
            }
            mastheadLinks={mastheadLinks}
            mastheadSubtitleAction={
              <>
                {mastheadDifficultyButton}
                {mastheadDetailsButton}
              </>
            }
            controls={difficultyControls}
            controlsClassName={DIFFICULTY_RANGE_INLINE}
            // The "You hold — <seat>" label rides directly above the hand inside SeatBoardLayout,
            // so it stays over the cards wherever the seat places them — not pinned to the panel
            // top. Suppress the panel-top heading here to avoid showing it twice.
            handLabel={null}
            callLabel={showingResult || answeredReview ? 'Your decision' : 'What is your call?'}
            hand={
              // The hand sits on the side that matches the seat, so the two read as one picture
              // of where the learner is sitting (feature 016 geometry, kept). The whole panel is
              // the shared one module practice uses — a hand is a hand whatever the session's
              // rules are, so this page assembles nothing of its own here.
              <HandPanel
                seat={currentProblem.decisionSeat}
                hand={currentProblem.playerHand}
                vulnerability={currentProblem.vulnerability}
                dealer={currentProblem.auction[0]?.seat}
              />
            }
            auction={
              // The shared centre panel, which owns its own scroll rig (the chevron hint, and the
              // rewind to the first bid) rather than making this page thread refs through.
              // `rewindOn` reproduces exactly what the page's own layout effect keyed on: a new
              // problem, or a verdict appearing. Its LENGTH must stay constant across renders.
              <AuctionPanel
                auction={currentProblem.auction}
                vulnerability={currentProblem.vulnerability}
                decisionSeat={currentProblem.decisionSeat}
                // No derived "?" here: every mode this page serves receives an auction that
                // already stops at the question, and the 3rd-level mode has a synthetic "?"
                // appended server-side (ThirdLevelBidDecisionService.Mask). Deriving another
                // would render two, and would break the "the ? is the LAST non-blank cell"
                // truncation contract the masked modes are asserted against.
                pendingSeat={null}
                rewindOn={[currentProblem.id, showingResult]}
              />
            }
            call={callPanel}
            // FR-031: Jeff's commentary where the hand has any. Feature 027: on a downloaded
            // tournament hand with none, the AI's stands in — saying "Information not available"
            // when the engine did not predict Jeff's call, rather than leaving no row at all.
            // Local problems keep the original behaviour: no comment, no panel.
            explain={
              // The editor review branch keeps its Jeff panel: `explainVariant` reports 'none'
              // for it, so that case is handled on its own terms here rather than losing the
              // comment the review already has.
              hasExpertComment(shownResult?.expertComment) ? (
                <ExplainPanel expertComment={shownResult?.expertComment} />
              ) : explainVariant === 'ai' ? (
                <ExplainPanel variant="ai" expertComment={lastResult?.aiComment} />
              ) : undefined
            }
            statusLine={
              // Desktop only: on phones the masthead's compact info row carries the essentials and
              // the ⓘ popup carries the words, so drawing the full line again under the frame
              // would repeat them in a space that has none to spare.
              <div className={MASTHEAD_FULL_BLOCK}>
                <StatusLine
                  position={currentIndex + 1}
                  total={totalProblems}
                  streak={streak}
                  score={modeConfig.showsScoring ? netScore : undefined}
                  // Revealed after the answer, as before — except in editor mode, where the
                  // assigned level is always shown so editors can re-assign without answering
                  // first (FR-031).
                  difficulty={revealDifficulty ? currentProblem.difficultyLevel : undefined}
                  // The Overcalling page is dual-source: a problem carries a serial, a tournament
                  // hand carries only its id. Naming which one is showing is what lets support
                  // paste either ?serial=N or ?id=N and land on the same item (FR-021).
                  idLabel={currentProblem.serial != null ? 'Serial' : 'ID'}
                  idValue={canSeeHandId ? (currentProblem.serial ?? currentProblem.id) : undefined}
                  scoringType={currentProblem.scoring}
                  tournamentTitle={currentProblem.source}
                  // The pin the reference design draws at the right end of this line. Its phone
                  // counterpart lives in the decision panel; both call the same handler and read
                  // the same state, so only one is ever on screen and they never disagree.
                  showReviewPin={canPinToReview}
                  onAddToReview={handleAddToReview}
                  isPinned={addedToReview}
                  pinDisabled={isAddingToReview}
                />
              </div>
            }
          />
        )}

        {/* Feature 013 editor mode (FR-029/FR-030): the reused 1-10 level buttons (assign
            auto-advances) and a right-aligned "Previous", below the frame. Admin/Editor only;
            exempt from the accessibility/phone-layout scope (FR-027). */}
        {isEditorMode && currentProblem && (
          <div
            className="mx-auto flex max-w-[1080px] flex-wrap items-center gap-3 px-4 pb-6"
            data-testid="third-level-editor-assign"
          >
            <AssignDifficultyControls
              currentLevel={currentProblem.difficultyLevel}
              disabled={isLoading}
              confirmationMessage={null}
              errorMessage={null}
              onAssign={assignThirdLevelDifficulty}
            />
            {/* FR-030: right edge aligned with the frame. */}
            <button
              type="button"
              onClick={goToPreviousProblem}
              disabled={isLoading || currentIndex === 0}
              data-testid="third-level-editor-previous"
              className="ml-auto rounded-md border border-brass bg-cream px-5 py-2.5 font-medium text-ink hover:bg-paper disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
          </div>
        )}

        {/* Screen-reader-only restatement of the sub-mode, so the announced page title carries
            it even though the visible masthead truncates. */}
        {subModeAriaSuffix && <span className="sr-only">{subModeAriaSuffix}</span>}

        {/* T137: Navigation away confirmation dialog for internal navigation (FR-069) */}
        <ConfirmDialog
          isOpen={blocker.state === 'blocked'}
          title="Leave Session?"
          message="You have an active session. If you leave, your progress will be lost."
          confirmLabel="Leave"
          cancelLabel="Stay"
          confirmVariant="danger"
          onConfirm={handleConfirmLeave}
          onCancel={handleCancelLeave}
        />

        {/* FR-109 (v1.9): Logout confirmation dialog during active session */}
        <ConfirmDialog
          isOpen={showLogoutConfirm}
          title="Logout?"
          message="You have an active session. If you logout, your progress will be lost."
          confirmLabel="Logout"
          cancelLabel="Stay"
          confirmVariant="danger"
          onConfirm={handleConfirmLogout}
          onCancel={handleCancelLogout}
        />

        {/* Feature 026: continue-or-restart prompt for Important Sequences / Review Problems.
            Non-dismissible — the user must choose before the session begins. */}
        {resumePrompt && (
          <ResumePromptModal
            isOpen={true}
            resumeQuestionNumber={resumePrompt.savedPosition + 1}
            totalQuestions={totalProblems}
            featureLabel={
              resumePrompt.feature === 'important-sequences'
                ? 'Important Problem Sequences'
                : 'Review Problems'
            }
            onContinue={continueFromSavedPosition}
            onRestart={restartFromBeginning}
          />
        )}

        {/* The difficulty range's home wherever the frame's controls row is not drawn — the same
            control, on the popup's cream surface rather than the felt. Deliberately NOT closed when
            the range changes: a learner narrowing 1-10 to 3-5 drags both handles, and re-opening the
            popup between them would make that two trips. */}
        {showDifficultyPopup && showMidSessionSlider && currentRange && currentProblem && (
          <DetailsPopup
            title="Difficulty Range"
            ariaLabel="Difficulty range"
            onClose={() => setShowDifficultyPopup(false)}
            screens={DIFFICULTY_RANGE_COMPACT}
            testId="difficulty-popup"
          >
            <DifficultyRangeControl
              surface="light"
              minLevel={currentRange.minLevel}
              maxLevel={currentRange.maxLevel}
              disabled={isSerialSearch}
              onChange={handleRangeChange}
            />
          </DetailsPopup>
        )}

        {/* The phone expansion of the masthead's compact info row: the same facts the desktop
            status line spells out, reached from the ⓘ. */}
        {showDetailsPopup && currentProblem && (
          <SessionDetailsPopup
            onClose={() => setShowDetailsPopup(false)}
            streak={streak}
            score={netScore}
            handId={canSeeHandId ? (currentProblem.serial ?? currentProblem.id) : null}
            idLabel={currentProblem.serial != null ? 'Serial' : 'ID'}
            scoringType={currentProblem.scoring ?? ''}
            difficulty={revealDifficulty ? currentProblem.difficultyLevel : null}
            tournamentTitle={currentProblem.source ?? null}
          />
        )}
      </SessionViewport>
    );
  }

  return null;
}
